import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from '@headlessui/react';
import { FiX } from 'react-icons/fi';

const SIZES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  full: 'sm:max-w-[95vw]',
};

/**
 * Shared modal: centered dialog on sm+ screens, bottom sheet on phones.
 * Headless UI Dialog provides focus trap, scroll lock, ESC and aria wiring.
 *
 * Props: isOpen, onClose, title, description, size, footer, headerSlot
 * (rendered under the title — used for the job-creation Stepper),
 * dismissible (controls the header X button + footer Cancel as the close
 * affordances), closeOnBackdrop (default false — clicking the backdrop or
 * pressing ESC does NOT close; the modal closes only via the X button, a
 * Cancel/footer button, or programmatically), initialFocus.
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  headerSlot,
  dismissible = true,
  closeOnBackdrop = false,
  initialFocus,
  bodyClassName = '',
  panelStyle,
  dir,
  saving = false,
  children,
}) {
  const { t } = useTranslation();
  return (
    <Transition show={!!isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={initialFocus}
        onClose={!saving && dismissible && closeOnBackdrop ? onClose : () => {}}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center sm:p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                dir={dir}
                style={panelStyle}
                className={`w-full ${SIZES[size] || SIZES.md} bg-surface text-content shadow-xl
                  rounded-t-2xl sm:rounded-2xl border-t sm:border border-edge
                  max-h-[92dvh] sm:max-h-[85vh] flex flex-col
                  pb-[env(safe-area-inset-bottom)] sm:pb-0`}
              >
                {(title || dismissible) && (
                  <div className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-edge">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {title && (
                          <DialogTitle className="text-base sm:text-lg font-semibold text-content">
                            {title}
                          </DialogTitle>
                        )}
                        {description && <p className="mt-0.5 text-sm text-content-secondary">{description}</p>}
                      </div>
                      {dismissible && (
                        <button
                          type="button"
                          onClick={saving ? undefined : onClose}
                          disabled={saving}
                          className={`shrink-0 p-2 -m-1 rounded-lg text-content-muted hover:text-content hover:bg-surface-2 transition-colors ${saving ? 'opacity-50 pointer-events-none' : ''}`}
                          aria-label={t('cm.close')}
                        >
                          <FiX size={18} />
                        </button>
                      )}
                    </div>
                    {headerSlot}
                  </div>
                )}

                <fieldset disabled={saving} aria-busy={saving} className="contents border-0 p-0 m-0 min-w-0">
                  <div className={`grow overflow-y-auto px-4 sm:px-6 py-4 ${bodyClassName}`}>{children}</div>

                  {footer && (
                    <div className="shrink-0 flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-edge bg-surface-2/50 rounded-b-none sm:rounded-b-2xl">
                      {footer}
                    </div>
                  )}
                </fieldset>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
